import {
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Info, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ROUTES } from '@/app/routes';
import { cn } from '@/lib/utils';
import {
  isDeepInFlightOutboxError,
  summarizeDeliveryError,
} from '@/features/moderation/lib/coopDelivery';
import {
  useInfrastructureHealth,
  useOutboxBacklog,
  useWakeGpu,
  type GpuWakeAction,
  type GpuWakeHistoryEntry,
  type InfrastructureHealth,
  type OutboxBacklogItem,
} from '../api.infrastructure';
import {
  deepPathFromHealth,
  normalizeModerationEngine,
  type DeepPathKind,
} from '../lib/deepPathFromHealth';
import {
  applyArmedGpuWake,
  gpuProvisioningElapsedLabel,
  gpuProvisioningInProgress,
  gpuRequestedInstanceLabel,
  gpuWakeActionAvailable,
  gpuWakeElapsedLabel,
  formatWakeHistoryWhen,
  gpuWakeFailureReason,
  gpuWakeHistoryDetail,
  gpuWakeHistoryHeadline,
  gpuWakeHistoryRows,
  gpuWakeInProgress,
  gpuWakeNoInstance,
  gpuWakeSummary,
  type ArmedGpuWake,
  wakeStartForAction,
} from '../lib/gpuWakeFromHealth';

type Tone = 'ok' | 'warn' | 'bad' | 'muted' | 'loading';

type HintCoords = { top: number; left: number; place: 'above' | 'below' };

function coordsForTrigger(el: HTMLElement, maxHalf = 160): HintCoords {
  const rect = el.getBoundingClientRect();
  const margin = 12;
  const below = window.innerHeight - rect.bottom;
  const above = rect.top;
  const place: HintCoords['place'] = below < 180 && above > below ? 'above' : 'below';
  const half = Math.min(maxHalf, Math.max(80, (window.innerWidth - margin * 2) / 2));
  return {
    top: place === 'below' ? rect.bottom + 6 : rect.top - 6,
    left: Math.min(
      Math.max(rect.left + rect.width / 2, margin + half),
      window.innerWidth - margin - half,
    ),
    place,
  };
}

function toneClasses(tone: Tone): string {
  switch (tone) {
    case 'ok':
      return 'border-lime-400/25 bg-lime-400/[0.07] text-lime-300';
    case 'warn':
      return 'border-amber-400/25 bg-amber-400/[0.07] text-amber-300';
    case 'bad':
      return 'border-rose-400/30 bg-rose-400/[0.08] text-rose-300';
    case 'loading':
      return 'border-white/10 bg-white/[0.03] text-zinc-500';
    default:
      return 'border-white/10 bg-white/[0.04] text-zinc-400';
  }
}

function Dot({ tone }: { tone: Tone }) {
  return (
    <span
      className={cn(
        'size-1.5 shrink-0 rounded-full',
        tone === 'ok' && 'bg-lime-400',
        tone === 'warn' && 'bg-amber-400',
        tone === 'bad' && 'bg-rose-400',
        (tone === 'muted' || tone === 'loading') && 'bg-zinc-500',
        tone === 'ok' && 'animate-pulse',
      )}
    />
  );
}

/** Hover/focus tooltip for stack chips. Portaled so page overflow does not clip it. */
function Chip({
  tone,
  label,
  title,
  tooltip,
}: {
  tone: Tone;
  label: string;
  /** Plain-text summary for aria-label / fallback. */
  title?: string | undefined;
  /** Rich hover content; falls back to `title` when omitted. */
  tooltip?: ReactNode;
}) {
  const tooltipId = useId();
  const hideTimer = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const [coords, setCoords] = useState<HintCoords | null>(null);
  const open = coords != null;
  const content = tooltip ?? title;

  function clearHide() {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }

  function show(el: HTMLElement) {
    if (content == null) return;
    clearHide();
    setCoords(coordsForTrigger(el, tooltip != null ? 180 : 160));
  }

  function hide() {
    clearHide();
    hideTimer.current = window.setTimeout(() => {
      setCoords(null);
      hideTimer.current = null;
    }, 120);
  }

  useEffect(() => {
    if (!open) return;
    const dismiss = () => {
      clearHide();
      setCoords(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);

  useEffect(() => () => clearHide(), []);

  return (
    <>
      <span
        aria-label={title ? `${label}. ${title}` : label}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={(event: MouseEvent<HTMLSpanElement>) => show(event.currentTarget)}
        onMouseLeave={hide}
        className={cn(
          'flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium tracking-wide',
          toneClasses(tone),
        )}
      >
        <Dot tone={tone} />
        {label}
      </span>
      {open && content != null
        ? createPortal(
            <span
              id={tooltipId}
              role="tooltip"
              className="fixed z-50 max-w-80 overflow-y-auto rounded-md border border-border bg-popover px-2.5 py-2 text-left text-[11px] font-normal normal-case leading-4 tracking-normal text-popover-foreground shadow-md"
              style={{
                top: coords.top,
                left: coords.left,
                maxHeight:
                  coords.place === 'below'
                    ? Math.max(96, window.innerHeight - coords.top - 12)
                    : Math.max(96, coords.top - 12),
                transform:
                  coords.place === 'below' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
              }}
              onMouseEnter={clearHide}
              onMouseLeave={hide}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

function mediaTone(data: InfrastructureHealth | undefined, loading: boolean, error: boolean): Tone {
  if (loading) return 'loading';
  if (error || !data?.mediaManagerReachable) return 'bad';
  return data.mediaManager?.ready ? 'ok' : 'bad';
}

function gpuTone(
  data: InfrastructureHealth | undefined,
  loading: boolean,
  error: boolean,
  nowMs: number = Date.now(),
): Tone {
  if (loading) return 'loading';
  if (error || !data?.mediaManagerReachable) return 'muted';
  const status = data.gpu?.status ?? 'unknown';
  if (gpuWakeNoInstance(data, nowMs)) return 'bad';
  if (gpuProvisioningInProgress(data, nowMs)) return 'warn';
  if (status === 'ready') return 'ok';
  // In-flight gpu_wake (scale-from-zero) — still warn, not idle Off.
  if (gpuWakeInProgress(data, nowMs)) return 'warn';
  if (status === 'offline') return 'warn';
  return 'muted';
}

function coopTone(data: InfrastructureHealth | undefined, loading: boolean, error: boolean): Tone {
  if (loading) return 'loading';
  if (error || !data?.mediaManagerReachable) return 'muted';
  const status = data.coop?.status ?? 'unknown';
  if (status === 'ready') return 'ok';
  if (status === 'unreachable') return 'bad';
  if (status === 'disabled' || status === 'not_configured') return 'muted';
  return 'muted';
}

function modeTone(data: InfrastructureHealth | undefined, loading: boolean): Tone {
  if (loading) return 'loading';
  if (!data?.tipmiApiReachable || !data.publicationMode) return 'muted';
  const mode = data.publicationMode;
  if (mode === 'HOLD') return 'ok';
  if (mode === 'LIVE_ENFORCE') return 'warn';
  if (mode === 'OBSERVE') return 'warn';
  if (mode === 'OFF') return 'muted';
  return 'muted';
}

function outboxTone(data: InfrastructureHealth | undefined, loading: boolean): Tone {
  if (loading) return 'loading';
  if (!data?.tipmiApiReachable) return 'muted';
  const status = data.outbox?.status ?? 'unknown';
  if (status === 'ok') return 'ok';
  if (status === 'backlog') return 'warn';
  if (status === 'dead') return 'bad';
  return 'muted';
}

function mediaLabel(data: InfrastructureHealth | undefined, loading: boolean, error: boolean): string {
  if (loading) return 'Media · …';
  if (error || !data?.mediaManagerReachable) return 'Media · Down';
  return data.mediaManager?.ready ? 'Media' : 'Media · Down';
}

function gpuEnvLabel(data: InfrastructureHealth | undefined): string | undefined {
  const ns = data?.gpu?.namespace?.trim();
  if (!ns) return undefined;
  return ns.toUpperCase();
}

/** Compact chip: status (+ replicas when scaling). Env / nodes / deep mode live in the tooltip. */
function gpuLabel(
  data: InfrastructureHealth | undefined,
  loading: boolean,
  error: boolean,
  nowMs: number = Date.now(),
): string {
  if (loading) return 'GPU · …';
  if (error || !data?.mediaManagerReachable) return 'GPU · —';
  const status = data.gpu?.status ?? 'unknown';
  const ready = data.gpu?.replicas;
  const desired = data.gpu?.desiredReplicas;
  const scaling =
    ready != null &&
    Number.isFinite(ready) &&
    desired != null &&
    Number.isFinite(desired) &&
    desired !== ready;
  const scale = scaling ? ` · ${ready}/${desired}` : '';
  if (status === 'ready') return `GPU · On${scale}`;
  const types = gpuRequestedInstanceLabel(data);
  const typeBit = types ? ` · ${types}` : '';
  // Settled capacity failure (not a timed-out wake ticker).
  if (gpuWakeNoInstance(data, nowMs)) {
    return `GPU · No instance${typeBit}`;
  }
  // Live gpu_wake in flight — wake elapsed + exact/allowlist instance types.
  if (gpuWakeInProgress(data, nowMs)) {
    const elapsed = gpuWakeElapsedLabel(data, nowMs);
    const head = elapsed ? `GPU · Waking · ${elapsed}` : 'GPU · Waking';
    return `${head}${typeBit}${scale}`;
  }
  // Pod desired, NodeClaim launching — provision elapsed + exact NodeClaim type.
  if (gpuProvisioningInProgress(data, nowMs)) {
    const elapsed = gpuProvisioningElapsedLabel(data, nowMs);
    const head = elapsed ? `GPU · Provisioning · ${elapsed}` : 'GPU · Provisioning';
    return `${head}${typeBit}${scale}`;
  }
  if (status === 'offline') {
    const why = gpuWakeFailureReason(data, nowMs);
    return why ? `GPU · Off · ${why}${scale}` : `GPU · Off${scale}`;
  }
  if (status === 'not_configured') return 'GPU · N/A';
  return 'GPU · ?';
}

function coopLabel(data: InfrastructureHealth | undefined, loading: boolean, error: boolean): string {
  if (loading) return 'Coop · …';
  if (error || !data?.mediaManagerReachable) return 'Coop · —';
  const status = data.coop?.status ?? 'unknown';
  if (status === 'ready') return 'Coop';
  if (status === 'unreachable') return 'Coop · Down';
  if (status === 'disabled') return 'Coop · Off';
  if (status === 'not_configured') return 'Coop · N/A';
  return 'Coop · ?';
}

/** Mode + engine in one chip to save horizontal space. */
function policyLabel(data: InfrastructureHealth | undefined, loading: boolean): string {
  if (loading) return '…';
  if (!data?.tipmiApiReachable) return '—';
  const mode = data.publicationMode ?? '—';
  const eng = engineFromHealth(data)
    ? engineDisplayLabel(engineFromHealth(data))
    : '—';
  return `${mode} · ${eng}`;
}

function policyTone(data: InfrastructureHealth | undefined, loading: boolean): Tone {
  const mode = modeTone(data, loading);
  const eng = engineTone(data, loading);
  if (loading) return 'loading';
  if (mode === 'bad' || eng === 'bad') return 'bad';
  if (mode === 'warn' || eng === 'warn') return 'warn';
  if (mode === 'ok' || eng === 'ok') return 'ok';
  return 'muted';
}

const MODERATION_ENGINES: ReadonlyArray<{
  value: string;
  label: string;
  summary: string;
}> = [
  {
    value: 'cloud',
    label: 'Cloud',
    summary: 'Cloud (Bedrock) classify + health record. Hide/publish from risk vectors / Coop.',
  },
  {
    value: 'gpu',
    label: 'GPU',
    summary: 'GPU moderation-deep for the whole task. No Cloud race; fail if GPU not ready.',
  },
  {
    value: 'gpu_with_cloud_fallback',
    label: 'GPU+Cloud',
    summary:
      'GPU when ready; if GPU is offline, Cloud deep runs immediately while GPU wake is armed in the background (default).',
  },
];

function engineDisplayLabel(wire: string): string {
  return MODERATION_ENGINES.find((e) => e.value === wire)?.label ?? wire;
}

function engineFromHealth(data: InfrastructureHealth | undefined): string {
  return normalizeModerationEngine(data?.moderationEngine ?? data?.taxonomySource);
}

function engineTone(data: InfrastructureHealth | undefined, loading: boolean): Tone {
  if (loading) return 'loading';
  if (!data?.tipmiApiReachable || !engineFromHealth(data)) return 'muted';
  const src = engineFromHealth(data);
  if (src === 'gpu') return 'ok';
  if (src === 'gpu_with_cloud_fallback') return 'ok';
  if (src === 'cloud') return 'muted';
  return 'muted';
}

function deepTone(
  data: InfrastructureHealth | undefined,
  loading: boolean,
  mediaError: boolean,
): Tone {
  if (loading) return 'loading';
  const kind = deepPathFromHealth(data, { mediaError }).kind;
  return deepKindTone(kind);
}

function deepKindTone(kind: DeepPathKind): Tone {
  switch (kind) {
    case 'gpu':
      return 'ok';
    case 'waiting_gpu':
    case 'waiting_then_cloud':
      return 'warn';
    case 'cloud':
      return 'muted';
    default:
      return 'muted';
  }
}

function deepLabel(
  data: InfrastructureHealth | undefined,
  loading: boolean,
  mediaError: boolean,
): string {
  return deepPathFromHealth(data, { loading, mediaError }).label;
}

function deepTitle(
  data: InfrastructureHealth | undefined,
  mediaError: boolean,
): string {
  return deepPathFromHealth(data, { mediaError }).summary;
}

function deepTooltip(
  data: InfrastructureHealth | undefined,
  mediaError: boolean,
): ReactNode {
  const view = deepPathFromHealth(data, { mediaError });
  const engine = engineFromHealth(data) || 'unknown';
  const gpuStatus = data?.mediaManagerReachable
    ? (data.gpu?.status ?? 'unknown')
    : 'unreachable';
  return (
    <div className="space-y-1.5 text-left">
      <p className="text-[11px] font-medium text-zinc-100">Effective deep path</p>
      <p className="text-[10px] leading-snug text-zinc-400">{view.summary}</p>
      <p className="text-[10px] leading-snug text-zinc-500">
        Env-level only (not which route a single post took). Engine{' '}
        <span className="font-medium text-zinc-300">{engine}</span>
        {' · '}
        GPU <span className="font-medium text-zinc-300">{gpuStatus}</span>
        {' · '}
        now <span className="font-medium text-zinc-200">{view.short}</span>
      </p>
    </div>
  );
}

function engineTitle(data: InfrastructureHealth | undefined): string | undefined {
  if (!data?.tipmiApiReachable) return 'tipmi-api stack-status unreachable';
  const src = engineFromHealth(data) || 'unknown';
  return `TIPMI_MODERATION_ENGINE=${src} · image_health hide off`;
}

function engineTooltip(data: InfrastructureHealth | undefined): ReactNode {
  const active = engineFromHealth(data);
  return (
    <div className="space-y-1.5 text-left">
      <p className="text-[11px] font-medium text-zinc-100">Moderation engine</p>
      <p className="text-[10px] leading-snug text-zinc-400">
        Assigns classify + health score (+ GPU risk vectors when GPU runs). TipMi never auto-hides
        from image health — visibility comes from risk vectors / Coop decide(). Default is
        GPU_WITH_CLOUD_FALLBACK.
      </p>
      <ul className="space-y-1">
        {MODERATION_ENGINES.map((entry) => {
          const isActive = entry.value === active;
          return (
            <li
              key={entry.value}
              className={cn(
                'rounded px-1.5 py-1 text-[10px] leading-snug',
                isActive ? 'bg-white/10 text-zinc-100' : 'text-zinc-400',
              )}
            >
              <span className="font-medium tracking-wide">{entry.label}</span>
              <span className="text-zinc-500"> ({entry.value})</span>
              {' — '}
              {entry.summary}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function outboxLabel(data: InfrastructureHealth | undefined, loading: boolean): string {
  if (loading) return 'Outbox · …';
  if (!data?.tipmiApiReachable) return 'Outbox · —';
  const status = data.outbox?.status ?? 'unknown';
  const pending = data.outbox?.pending ?? 0;
  const dead = data.outbox?.dead ?? 0;
  if (status === 'dead') return `Outbox · Dead ${dead}`;
  if (status === 'backlog') return `Outbox · ${pending}`;
  if (status === 'ok') return pending > 0 ? `Outbox · ${pending}` : 'Outbox';
  return 'Outbox · ?';
}

function crashLoopsTone(
  data: InfrastructureHealth | undefined,
  loading: boolean,
  error: boolean,
): Tone {
  if (loading) return 'loading';
  if (error || !data?.mediaManagerReachable) return 'muted';
  const total = data.crashLoops?.total ?? 0;
  if (total > 0) return 'bad';
  return 'ok';
}

function crashLoopsLabel(
  data: InfrastructureHealth | undefined,
  loading: boolean,
  error: boolean,
): string {
  if (loading) return 'Pods · …';
  if (error || !data?.mediaManagerReachable) return 'Pods · —';
  const total = data.crashLoops?.total ?? 0;
  if (total > 0) return `Pods · Crash ${total}`;
  return 'Pods';
}

function crashLoopsTitle(data: InfrastructureHealth | undefined): string {
  if (!data?.mediaManagerReachable) {
    return 'CrashLoopBackOff probe via media-manager. Requires reachable media-manager.';
  }
  const total = data.crashLoops?.total ?? 0;
  const workloads = data.crashLoops?.workloads ?? [];
  if (total <= 0) {
    return 'No CrashLoopBackOff pods on probed moderation workloads (media-manager, provisioned-delivery-worker, Coop, GPU).';
  }
  const lines = workloads
    .filter((wl) => (wl.crashLooping ?? 0) > 0)
    .map((wl) => {
      const names = (wl.pods ?? [])
        .map((p) => p.name)
        .filter(Boolean)
        .join(', ');
      return `${wl.deployment ?? wl.id ?? 'workload'} (${wl.namespace ?? '?'}) · ${wl.crashLooping}${names ? ` · ${names}` : ''}`;
    });
  return lines.length > 0
    ? `CrashLoopBackOff:\n${lines.join('\n')}`
    : `${total} CrashLoopBackOff pod(s) detected.`;
}

const PUBLICATION_MODES: ReadonlyArray<{
  value: string;
  summary: string;
}> = [
  {
    value: 'HOLD',
    summary: 'Hold new posts (PUB→WAT) until Coop/admin decide. Product default.',
  },
  {
    value: 'LIVE_ENFORCE',
    summary: 'Go live first; non-approve outcomes may temporarily unpublish.',
  },
  {
    value: 'OBSERVE',
    summary: 'Go live immediately; ledger only — no Coop case on create.',
  },
  {
    value: 'OFF',
    summary: 'No publication moderation on create.',
  },
];

const DEEP_MODES: ReadonlyArray<{
  value: string;
  summary: string;
}> = [
  {
    value: 'real',
    summary: 'Full GPU deep scan; model scores can drive enforcement.',
  },
  {
    value: 'shadow',
    summary: 'Runs GPU models for scoring/logging only; does not enforce.',
  },
  {
    value: 'heuristic',
    summary: 'Heuristics only; no real GPU model pass (chip omits this label).',
  },
];

function mediaTitle(data: InfrastructureHealth | undefined): string {
  if (!data?.mediaManagerReachable) {
    return data?.error
      ? `Media-manager unreachable: ${data.error}`
      : 'Media-manager unreachable — upload/processing probes cannot run.';
  }
  const parts: string[] = [
    data.mediaManager?.ready
      ? 'Media-manager is healthy and accepting work.'
      : 'Media-manager reachable but not ready.',
  ];
  if (data.mediaManager?.databaseConnected != null) {
    parts.push(`DB ${data.mediaManager.databaseConnected ? 'ok' : 'down'}`);
  }
  if (data.mediaManager?.rabbitmqConnected != null) {
    parts.push(`RabbitMQ ${data.mediaManager.rabbitmqConnected ? 'ok' : 'down'}`);
  }
  return parts.join(' · ');
}

function gpuReplicaTitle(data: InfrastructureHealth | undefined): string | undefined {
  const ready = data?.gpu?.replicas;
  if (ready == null || !Number.isFinite(ready)) return undefined;
  const desired = data?.gpu?.desiredReplicas;
  if (desired != null && Number.isFinite(desired)) {
    return `Replicas ${ready}/${desired}`;
  }
  return `Replicas ${ready}`;
}

function gpuCapacityTitle(data: InfrastructureHealth | undefined): string | undefined {
  const cap = data?.gpu?.capacity;
  if (!cap || cap.readyNodes == null || !Number.isFinite(cap.readyNodes)) return undefined;
  const limit = cap.nodePoolLimitGpus ?? cap.desiredNodes;
  const nodes =
    limit != null && Number.isFinite(limit)
      ? `Nodes ${cap.readyNodes}/${limit}`
      : `Nodes ${cap.readyNodes}`;
  const parts = [nodes];
  const types = (cap.instanceTypes ?? []).filter(Boolean);
  if (types.length > 0) parts.push(types.join(', '));
  const caps = (cap.capacityTypes ?? []).filter(Boolean);
  if (caps.length > 0) parts.push(caps.join(', '));
  const zones = (cap.zones ?? []).filter(Boolean);
  if (zones.length > 0) parts.push(zones.join(', '));
  if (cap.status && cap.status !== 'ready') parts.push(cap.status.replace(/_/g, ' '));
  return parts.join(' · ');
}

function gpuTitle(data: InfrastructureHealth | undefined, nowMs: number = Date.now()): string {
  if (!data?.mediaManagerReachable) {
    return 'Vision / GPU probe via media-manager. Requires reachable media-manager.';
  }
  const status = data.gpu?.status ?? 'unknown';
  const env = gpuEnvLabel(data);
  const cluster = env ? `Cluster ${env}` : undefined;
  const deepMode = data.gpu?.deepMode;
  const deepMatch = DEEP_MODES.find((entry) => entry.value === deepMode);
  const deep = deepMode
    ? deepMatch
      ? `Deep mode ${deepMatch.value}: ${deepMatch.summary}`
      : `Deep mode: ${deepMode}`
    : undefined;
  const replicas = gpuReplicaTitle(data);
  const capacity = gpuCapacityTitle(data);
  if (status === 'ready') {
    return [
      'Vision / GPU model server is responding.',
      cluster,
      replicas,
      capacity,
      deep,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (gpuWakeNoInstance(data, nowMs) || gpuWakeInProgress(data, nowMs)) {
    return [
      gpuWakeSummary(data, nowMs) ??
        (gpuWakeNoInstance(data, nowMs)
          ? 'No GPU instance (wake timed out / AWS capacity).'
          : 'GPU wake in progress (scale-from-zero).'),
      cluster,
      replicas,
      capacity,
      deep,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (status === 'offline') {
    return [
      gpuWakeSummary(data, nowMs) ??
        (gpuProvisioningInProgress(data, nowMs)
          ? 'GPU pod desired but no Ready GPU node yet (instance capacity / AZ).'
          : 'GPU scaled to zero or not responding (idle is normal; wakes on deep scan).'),
      cluster,
      replicas,
      capacity,
      deep,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (status === 'not_configured') {
    return 'GPU not configured (SAFEGUARD_INFERENCE_URL unset in this environment).';
  }
  return (
    [cluster, replicas, capacity, deep].filter(Boolean).join(' · ') ||
    'GPU status unknown.'
  );
}

function gpuTooltip(data: InfrastructureHealth | undefined, nowMs: number = Date.now()): ReactNode {
  const current = data?.mediaManagerReachable ? data.gpu?.deepMode : undefined;
  const status = data?.gpu?.status ?? 'unknown';
  const env = gpuEnvLabel(data);
  const replicas = gpuReplicaTitle(data);
  const capacity = gpuCapacityTitle(data);
  const types = gpuRequestedInstanceLabel(data);
  const wakeLine = gpuWakeSummary(data, nowMs);
  const statusLine =
    !data?.mediaManagerReachable
      ? 'Requires reachable media-manager'
      : status === 'ready'
        ? 'Model server is responding'
        : gpuWakeNoInstance(data, nowMs)
          ? 'No GPU instance (wake timed out / AWS capacity)'
          : gpuWakeInProgress(data, nowMs)
            ? 'Wake in progress (gpu_wake active)'
            : gpuProvisioningInProgress(data, nowMs)
              ? (() => {
                  const elapsed = gpuProvisioningElapsedLabel(data, nowMs);
                  return elapsed
                    ? `Waiting for GPU instance · ${elapsed}`
                    : 'Waiting for GPU instance capacity';
                })()
              : status === 'offline'
                ? 'Scaled to zero or not responding (idle is normal)'
                : status === 'not_configured'
                  ? 'Not configured (SAFEGUARD_INFERENCE_URL unset)'
                  : 'Status unknown';

  return (
    <div className="space-y-2">
      <div>
        <p className="font-medium text-popover-foreground">Vision / GPU</p>
        <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
          {statusLine}
          {env ? ` · Cluster ${env}` : null}
          {replicas ? ` · ${replicas}` : null}
          {capacity ? ` · ${capacity}` : null}
          {types ? ` · requesting ${types}` : null}
          {wakeLine ? ` · ${wakeLine}` : null}
          {current ? (
            <>
              {' '}
              · deep <span className="font-medium text-zinc-200">{current}</span>
            </>
          ) : null}
        </p>
      </div>
      <ul className="space-y-1.5 border-t border-border/60 pt-2">
        {DEEP_MODES.map((entry) => {
          const active = entry.value === current;
          return (
            <li key={entry.value} className="leading-snug">
              <span
                className={cn(
                  'font-semibold',
                  active ? 'text-lime-300' : 'text-zinc-300',
                )}
              >
                {entry.value}
                {active ? ' · current' : ''}
              </span>
              <span className="text-muted-foreground"> — {entry.summary}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function coopTitle(data: InfrastructureHealth | undefined): string {
  if (!data?.mediaManagerReachable) {
    return 'Coop moderation service probe via media-manager. Requires reachable media-manager.';
  }
  const status = data.coop?.status ?? 'unknown';
  if (status === 'ready') return 'Coop server accepts HTTP connections from media-manager.';
  if (status === 'unreachable') return 'Coop server not reachable from media-manager.';
  if (status === 'disabled') return 'Coop disabled (COOP_ENABLED is false).';
  if (status === 'not_configured') return 'Coop not configured (COOP_API_URL missing).';
  return 'Coop status unknown.';
}

function modeTitle(data: InfrastructureHealth | undefined): string {
  if (!data?.tipmiApiReachable) {
    return 'Publication mode from tipmi-api. Requires reachable tipmi-api.';
  }
  const mode = data.publicationMode;
  const match = PUBLICATION_MODES.find((entry) => entry.value === mode);
  if (match) return `Current mode ${match.value}: ${match.summary}`;
  return mode
    ? `Current publication mode: ${mode}.`
    : 'Publication mode unavailable.';
}

function modeTooltip(data: InfrastructureHealth | undefined): ReactNode {
  const current = data?.tipmiApiReachable ? data.publicationMode : undefined;
  return (
    <div className="space-y-2">
      <div>
        <p className="font-medium text-popover-foreground">Publication mode</p>
        <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
          How tipmi-api gates new posts on create
          {current ? (
            <>
              {' '}
              · current <span className="font-medium text-zinc-200">{current}</span>
            </>
          ) : !data ? (
            ' · …'
          ) : data.tipmiApiReachable ? (
            ' · publication mode unavailable'
          ) : (
            ' · tipmi-api unreachable'
          )}
        </p>
      </div>
      <ul className="space-y-1.5 border-t border-border/60 pt-2">
        {PUBLICATION_MODES.map((entry) => {
          const active = entry.value === current;
          return (
            <li key={entry.value} className="leading-snug">
              <span
                className={cn(
                  'font-semibold',
                  active ? 'text-lime-300' : 'text-zinc-300',
                )}
              >
                {entry.value}
                {active ? ' · current' : ''}
              </span>
              <span className="text-muted-foreground"> — {entry.summary}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function policyTitle(data: InfrastructureHealth | undefined): string {
  const mode = modeTitle(data);
  const eng = engineTitle(data);
  return eng ? `${mode} · ${eng}` : mode;
}

function policyTooltip(data: InfrastructureHealth | undefined): ReactNode {
  return (
    <div className="space-y-3">
      {modeTooltip(data)}
      <div className="border-t border-border/60 pt-2">{engineTooltip(data)}</div>
    </div>
  );
}

function outboxTitle(data: InfrastructureHealth | undefined): string {
  if (!data?.tipmiApiReachable) {
    return 'tipmi-api outbox backlog. Requires reachable tipmi-api.';
  }
  const pending = data.outbox?.pending ?? 0;
  const dead = data.outbox?.dead ?? 0;
  return `Async outbox jobs waiting for delivery. Pending/retry: ${pending} · Dead: ${dead}. Open stack details and choose View backlog.`;
}

/** Classify backlog rows so GPU/deep waits are not painted as failures. */
function outboxItemKind(
  item: OutboxBacklogItem,
): 'dead' | 'in_flight' | 'failing' | 'queued' {
  const status = (item.status ?? '').toUpperCase();
  if (status === 'DEAD') return 'dead';
  if (isDeepInFlightOutboxError(item.lastError, item.lastStage)) return 'in_flight';
  if (status === 'RETRY') return 'failing';
  return 'queued';
}

function outboxItemTone(kind: ReturnType<typeof outboxItemKind>): Tone {
  switch (kind) {
    case 'dead':
      return 'bad';
    case 'failing':
      return 'warn';
    case 'in_flight':
      return 'ok';
    default:
      return 'muted';
  }
}

function outboxItemBadge(kind: ReturnType<typeof outboxItemKind>, status: string): string {
  switch (kind) {
    case 'in_flight':
      return 'IN FLIGHT';
    case 'failing':
      return status || 'RETRY';
    case 'dead':
      return 'DEAD';
    default:
      return status || 'PENDING';
  }
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.endsWith('Z') || value.includes('+') ? value : `${value}Z`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function OutboxItemCard({ item }: { item: OutboxBacklogItem }) {
  const status = item.status ?? 'UNKNOWN';
  const kind = outboxItemKind(item);
  const tone = outboxItemTone(kind);
  const badge = outboxItemBadge(kind, status);
  const title =
    item.postId != null
      ? `Post ${item.postId}`
      : (item.caseId ?? `Outbox #${item.id ?? '?'}`);
  const nextLabel = kind === 'in_flight' ? 'Next poll' : 'Next retry';
  const summary =
    kind === 'in_flight'
      ? 'Deep scan in progress — GPU may be waking; tipmi-api will poll again shortly.'
      : summarizeDeliveryError(item.lastError, item.lastStage);

  return (
    <li className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          {item.postId != null ? (
            <Link
              to={ROUTES.contentDetails(String(item.postId))}
              className="text-sm font-medium text-white hover:text-lime-300"
            >
              {title}
            </Link>
          ) : (
            <p className="text-sm font-medium text-white">{title}</p>
          )}
          <p className="truncate font-mono text-[10px] text-zinc-500">{item.caseId ?? '—'}</p>
        </div>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            toneClasses(tone),
          )}
        >
          <Dot tone={tone} />
          {badge}
        </span>
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
        <div>
          <dt className="text-zinc-500">Attempts</dt>
          <dd className="font-medium text-zinc-200">{item.attemptCount ?? 0}</dd>
        </div>
        <div>
          <dt className="text-zinc-500">{nextLabel}</dt>
          <dd className="font-medium text-zinc-200">{formatWhen(item.nextAttemptAt)}</dd>
        </div>
        <div>
          <dt className="text-zinc-500">Stage</dt>
          <dd className="font-medium text-zinc-200">{item.lastStage || '—'}</dd>
        </div>
        <div>
          <dt className="text-zinc-500">Updated</dt>
          <dd className="font-medium text-zinc-200">{formatWhen(item.modifiedAt)}</dd>
        </div>
      </dl>

      {summary ? (
        <p
          className={cn(
            'mt-2 break-words rounded-md border px-2 py-1.5 text-[10px] leading-relaxed',
            kind === 'in_flight' || kind === 'queued'
              ? 'border-lime-400/15 bg-lime-400/[0.05] text-lime-100/90'
              : 'border-rose-400/15 bg-rose-400/[0.05] font-mono text-rose-200/90',
          )}
          title={item.lastError ?? undefined}
        >
          {summary}
        </p>
      ) : null}
    </li>
  );
}

function OutboxBacklogDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading, isError, isFetching, refetch, error } = useOutboxBacklog(open);
  const items = data?.items ?? [];
  const pending = data?.pending ?? 0;
  const dead = data?.dead ?? 0;
  const inFlight = items.filter((item) => outboxItemKind(item) === 'in_flight').length;
  const failing = items.filter((item) => outboxItemKind(item) === 'failing').length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden border-border/50 bg-[#161618] p-0 text-popover-foreground sm:max-w-xl">
        <DialogHeader className="space-y-1 border-b border-white/8 px-5 py-4 text-left">
          <div className="flex items-start justify-between gap-3 pr-6">
            <div>
              <DialogTitle className="text-base text-white">Moderation outbox</DialogTitle>
              <DialogDescription className="mt-1 text-[11px] text-muted-foreground">
                TipMi → media-manager · pending/retry {pending}
                {inFlight > 0 ? ` · deep in flight ${inFlight}` : ''}
                {failing > 0 ? ` · failing ${failing}` : ''}
                {` · dead ${dead}`}
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={() => void refetch()}
              disabled={isFetching}
              className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-medium text-zinc-300 hover:bg-white/[0.08] hover:text-white disabled:opacity-50"
            >
              <RefreshCw className={cn('size-3', isFetching && 'animate-spin')} aria-hidden />
              Refresh
            </button>
          </div>
        </DialogHeader>

        <div className="max-h-[min(60vh,28rem)] overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Loading outbox…</p>
          ) : isError && !data ? (
            <p className="rounded-lg border border-rose-400/20 bg-rose-400/[0.06] px-3 py-2 text-[11px] text-rose-200/90">
              {error instanceof Error ? error.message : 'Failed to load outbox backlog'}
            </p>
          ) : (
            <>
              {isError ? (
                <p className="mb-3 rounded-lg border border-rose-400/20 bg-rose-400/[0.06] px-3 py-2 text-[11px] text-rose-200/90">
                  {error instanceof Error ? error.message : 'Failed to refresh outbox backlog'}{' '}
                  Showing the last loaded backlog.
                </p>
              ) : null}
              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground">No pending, retrying, or dead outbox rows.</p>
              ) : (
                <ul className="space-y-2.5">
                  {items.map((item) => (
                    <OutboxItemCard key={item.id ?? `${item.caseId}-${item.status}`} item={item} />
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function boolLabel(value: boolean | null | undefined): string {
  if (value == null) return '—';
  return value ? 'ok' : 'down';
}

function DetailRow({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  tone?: Tone;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        {hint ? <p className="mt-0.5 text-[10px] leading-snug text-zinc-500">{hint}</p> : null}
      </div>
      <p
        className={cn(
          'shrink-0 text-right text-[11px] font-medium tabular-nums',
          tone === 'ok' && 'text-lime-300',
          tone === 'warn' && 'text-amber-300',
          tone === 'bad' && 'text-rose-300',
          (!tone || tone === 'muted' || tone === 'loading') && 'text-zinc-200',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">{title}</h3>
      <div className="space-y-2.5 rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
        {children}
      </div>
    </section>
  );
}

function WakeHistoryList({ data }: { data: InfrastructureHealth | undefined }) {
  const rows = gpuWakeHistoryRows(data);
  if (rows.length === 0) {
    return <p className="text-[10px] leading-snug text-zinc-500">No wake errors yet.</p>;
  }
  return (
    <div className="space-y-1.5 border-t border-white/8 pt-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
        Wake history
      </p>
      <ul className="space-y-1.5">
        {rows.map((row, index) => (
          <WakeHistoryItem key={`${row.workId ?? 'wake'}-${index}`} row={row} />
        ))}
      </ul>
    </div>
  );
}

function WakeHistoryItem({ row }: { row: GpuWakeHistoryEntry }) {
  const headline = gpuWakeHistoryHeadline(row);
  const when = formatWakeHistoryWhen(row.completedAt ?? row.enqueuedAt);
  const detail = gpuWakeHistoryDetail(row);
  return (
    <li className="text-[10px] leading-snug">
      <p className="text-rose-200">
        {headline}
        {when ? ` · ${when}` : ''}
        {row.workId != null ? ` · #${row.workId}` : ''}
      </p>
      {detail ? <p className="text-zinc-500">{detail}</p> : null}
    </li>
  );
}

function modeDetailHint(mode: string | null | undefined): string | undefined {
  return PUBLICATION_MODES.find((entry) => entry.value === mode)?.summary;
}

function deepModeHint(mode: string | null | undefined): string | undefined {
  const match = DEEP_MODES.find((entry) => entry.value === mode);
  if (match) return match.summary;
  return 'real: enforce · shadow: score only · heuristic: no GPU models';
}

function wakeActionToast(result: GpuWakeAction | undefined) {
  const status = result?.status ?? 'error';
  if (status === 'enqueued') {
    toast.success(result?.workId != null ? `GPU wake enqueued · work #${result.workId}` : 'GPU wake enqueued');
    return;
  }
  if (status === 'already_ready') {
    toast.success('GPU is already up');
    return;
  }
  if (status === 'already_active') {
    toast.message(
      result?.workId != null ? `GPU wake already in progress · work #${result.workId}` : 'GPU wake already in progress',
    );
    return;
  }
  if (status === 'disabled') {
    toast.error(result?.error || 'GPU wake is disabled');
    return;
  }
  toast.error(result?.error || 'Failed to wake GPU');
}

function StackDetailsPopover({
  data,
  isLoading,
  isError,
  isFetching,
  onRefresh,
  onWake,
  onOpenOutbox,
  waking,
  nowMs = Date.now(),
}: {
  data: InfrastructureHealth | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  onWake: () => void;
  onOpenOutbox: () => void;
  waking: boolean;
  nowMs?: number;
}) {
  const mediaReady = !isLoading && !isError && data?.mediaManagerReachable === true;
  const tipmiReady = !isLoading && data?.tipmiApiReachable === true;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Stack details"
          aria-label="Stack details"
          className={cn(
            'flex items-center justify-center rounded-md border px-1.5 py-1',
            'border-white/10 bg-white/[0.04] text-zinc-300 hover:bg-white/[0.08] hover:text-white',
          )}
        >
          <Info className="size-3 opacity-80" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        collisionPadding={16}
        className="w-[min(22rem,calc(100vw-2rem))] space-y-3 overflow-y-auto border-border/50 bg-[#161618] p-4 text-popover-foreground shadow-xl"
        style={{ maxHeight: 'var(--radix-popover-content-available-height, calc(100dvh - 8rem))' }}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-white">Moderation stack</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Live probes · hover a chip for detail
            </p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isFetching}
            className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-medium text-zinc-300 hover:bg-white/[0.08] hover:text-white disabled:opacity-50"
          >
            <RefreshCw className={cn('size-3', isFetching && 'animate-spin')} aria-hidden />
            Refresh
          </button>
        </div>

        {isLoading ? (
          <p className="text-xs text-muted-foreground">Loading stack status…</p>
        ) : (
          <div className="space-y-3">
            <Section title="Publication">
              <DetailRow
                label="Mode"
                value={tipmiReady ? (data?.publicationMode ?? '—') : '—'}
                hint={modeDetailHint(data?.publicationMode)}
                tone={modeTone(data, false)}
              />
              <DetailRow
                label="tipmi-api Coop"
                value={
                  tipmiReady
                    ? data?.tipmiCoopEnabled == null
                      ? '—'
                      : data.tipmiCoopEnabled
                        ? 'enabled'
                        : 'disabled'
                    : 'unreachable'
                }
                tone={
                  !tipmiReady
                    ? 'bad'
                    : data?.tipmiCoopEnabled
                      ? 'ok'
                      : 'muted'
                }
              />
              <DetailRow
                label="Engine"
                value={
                  tipmiReady
                    ? engineFromHealth(data)
                      ? engineDisplayLabel(engineFromHealth(data))
                      : '—'
                    : '—'
                }
                hint={
                  MODERATION_ENGINES.find((e) => e.value === engineFromHealth(data))?.summary
                }
                tone={engineTone(data, false)}
              />
              <DetailRow
                label="Effective deep path"
                value={
                  tipmiReady && mediaReady
                    ? deepPathFromHealth(data, { mediaError: isError }).short
                    : '—'
                }
                hint={deepPathFromHealth(data, { mediaError: isError }).summary}
                tone={deepTone(data, false, isError)}
              />
              <DetailRow
                label="image_health TipMi hide"
                value={tipmiReady ? 'off' : '—'}
                hint="Visibility from risk vectors / Coop decide() — never TipMi imageHealth auto-hide."
                tone={tipmiReady ? 'ok' : 'muted'}
              />
            </Section>

            <Section title="Media manager">
              <DetailRow
                label="Service"
                value={
                  mediaReady
                    ? data?.mediaManager?.ready
                      ? 'ready'
                      : 'not ready'
                    : 'unreachable'
                }
                tone={mediaTone(data, false, isError)}
              />
              <DetailRow
                label="Database"
                value={mediaReady ? boolLabel(data?.mediaManager?.databaseConnected) : '—'}
                tone={
                  data?.mediaManager?.databaseConnected === true
                    ? 'ok'
                    : data?.mediaManager?.databaseConnected === false
                      ? 'bad'
                      : 'muted'
                }
              />
              <DetailRow
                label="RabbitMQ"
                value={mediaReady ? boolLabel(data?.mediaManager?.rabbitmqConnected) : '—'}
                tone={
                  data?.mediaManager?.rabbitmqConnected === true
                    ? 'ok'
                    : data?.mediaManager?.rabbitmqConnected === false
                      ? 'bad'
                      : 'muted'
                }
              />
            </Section>

            <Section title="Coop">
              <DetailRow
                label="Status"
                value={mediaReady ? (data?.coop?.status ?? 'unknown') : '—'}
                hint={coopTitle(data)}
                tone={coopTone(data, false, isError)}
              />
              <DetailRow
                label="Configured"
                value={
                  mediaReady
                    ? data?.coop?.configured
                      ? 'yes'
                      : 'no'
                    : '—'
                }
              />
            </Section>

            <Section title="GPU / vision">
              <DetailRow
                label="Status"
                value={
                  mediaReady
                    ? data?.gpu?.status === 'ready'
                      ? 'ready'
                      : gpuWakeNoInstance(data, nowMs)
                        ? 'no instance'
                        : gpuWakeInProgress(data, nowMs)
                          ? 'waking'
                          : (data?.gpu?.status ?? 'unknown')
                    : '—'
                }
                hint={
                  data?.gpu?.status === 'ready'
                    ? undefined
                    : gpuWakeInProgress(data, nowMs) || gpuWakeNoInstance(data, nowMs)
                      ? gpuWakeSummary(data, nowMs)
                      : data?.gpu?.status === 'offline'
                        ? 'Idle / KEDA scale-to-zero is normal'
                        : undefined
                }
                tone={gpuTone(data, false, isError, nowMs)}
              />
              <DetailRow
                label="Wake"
                value={
                  mediaReady
                    ? gpuWakeNoInstance(data, nowMs)
                      ? 'timed out · no instance'
                      : gpuWakeInProgress(data, nowMs)
                        ? data?.gpu?.wake?.state
                          ? `active · ${data.gpu.wake.state}`
                          : 'active'
                        : data?.gpu?.wake?.lastError
                          ? 'last failed'
                          : data?.gpu?.wake?.enabled === false
                            ? 'disabled'
                            : 'idle'
                    : '—'
                }
                hint={gpuWakeSummary(data, nowMs) ?? 'Live gpu_wake ProvisionedWork from media-manager'}
                tone={
                  gpuWakeNoInstance(data, nowMs)
                    ? 'bad'
                    : gpuWakeInProgress(data, nowMs)
                      ? 'warn'
                      : data?.gpu?.wake?.lastError
                        ? 'bad'
                        : 'muted'
                }
              />
              {gpuWakeActionAvailable(data, nowMs) ? (
                <div className="flex items-center justify-between gap-3 pt-0.5">
                  <p className="text-[10px] leading-snug text-zinc-500">
                    Enqueue gpu_wake so KEDA scales the GPU pod. Does not wait for Ready.
                  </p>
                  <button
                    type="button"
                    onClick={onWake}
                    disabled={waking || !mediaReady}
                    className="shrink-0 rounded-md border border-amber-400/25 bg-amber-400/[0.08] px-2 py-1 text-[10px] font-medium text-amber-200 hover:bg-amber-400/[0.14] disabled:opacity-50"
                  >
                    {waking ? 'Waking…' : 'Wake GPU'}
                  </button>
                </div>
              ) : null}
              {mediaReady ? <WakeHistoryList data={data} /> : null}
              <DetailRow
                label="Requesting"
                value={
                  mediaReady
                    ? gpuRequestedInstanceLabel(data) ??
                      (data?.gpu?.capacity?.requestedInstanceTypes?.length
                        ? data.gpu.capacity.requestedInstanceTypes.join(', ')
                        : '—')
                    : '—'
                }
                hint={
                  data?.gpu?.capacity?.provisioningInstanceTypes?.length
                    ? 'Exact type from in-flight Karpenter NodeClaim'
                    : 'Instance types allowed by the gpu NodePool (allowlist)'
                }
              />
              <DetailRow
                label="Provisioning"
                value={
                  mediaReady && gpuProvisioningInProgress(data, nowMs)
                    ? (() => {
                        const elapsed = gpuProvisioningElapsedLabel(data, nowMs);
                        const n = data?.gpu?.capacity?.provisioningNodeclaims;
                        const bits = [elapsed ?? 'in progress'];
                        if (n != null && Number.isFinite(n)) {
                          bits.push(`${n} NodeClaim${n === 1 ? '' : 's'}`);
                        }
                        return bits.join(' · ');
                      })()
                    : '—'
                }
                hint="Age of oldest non-Ready GPU NodeClaim"
              />
              <DetailRow
                label="Cluster"
                value={
                  mediaReady
                    ? gpuEnvLabel(data) ??
                      (data?.gpu?.configured ? '—' : 'N/A')
                    : '—'
                }
                hint="Kubernetes namespace hosting moderation-gpu-server (DEV and STG may share DEV)"
              />
              <DetailRow
                label="Replicas"
                value={
                  mediaReady
                    ? data?.gpu?.replicas == null
                      ? '—'
                      : data.gpu.desiredReplicas != null
                        ? `${data.gpu.replicas}/${data.gpu.desiredReplicas}`
                        : String(data.gpu.replicas)
                    : '—'
                }
                hint="Ready / desired moderation-gpu-server pods"
                tone={
                  data?.gpu?.replicas != null && data.gpu.replicas > 0
                    ? 'ok'
                    : data?.gpu?.status === 'offline'
                      ? 'warn'
                      : 'muted'
                }
              />
              <DetailRow
                label="Nodes"
                value={
                  mediaReady
                    ? data?.gpu?.capacity?.readyNodes == null
                      ? '—'
                      : (() => {
                          const ready = data.gpu.capacity!.readyNodes!;
                          const limit =
                            data.gpu.capacity!.nodePoolLimitGpus ??
                            data.gpu.capacity!.desiredNodes;
                          return limit != null ? `${ready}/${limit}` : String(ready);
                        })()
                    : '—'
                }
                hint={
                  mediaReady && data?.gpu?.capacity
                    ? [
                        data.gpu.capacity.instanceTypes?.length
                          ? data.gpu.capacity.instanceTypes.join(', ')
                          : null,
                        data.gpu.capacity.capacityTypes?.length
                          ? data.gpu.capacity.capacityTypes.join(', ')
                          : null,
                        data.gpu.capacity.zones?.length
                          ? data.gpu.capacity.zones.join(', ')
                          : null,
                        data.gpu.capacity.status
                          ? data.gpu.capacity.status.replace(/_/g, ' ')
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Ready GPU nodes (workload=gpu)'
                    : 'Ready GPU nodes (workload=gpu)'
                }
                tone={
                  data?.gpu?.capacity?.status === 'provisioning'
                    ? 'warn'
                    : data?.gpu?.capacity?.readyNodes != null &&
                        data.gpu.capacity.readyNodes > 0
                      ? 'ok'
                      : data?.gpu?.status === 'offline'
                        ? 'warn'
                        : 'muted'
                }
              />
              <DetailRow
                label="Deep mode"
                value={mediaReady ? (data?.gpu?.deepMode ?? '—') : '—'}
                hint={deepModeHint(data?.gpu?.deepMode)}
              />
            </Section>

            <Section title="Outbox">
              <DetailRow
                label="Pending / retry"
                value={tipmiReady ? String(data?.outbox?.pending ?? 0) : '—'}
                tone={outboxTone(data, false)}
              />
              <DetailRow
                label="Dead"
                value={tipmiReady ? String(data?.outbox?.dead ?? 0) : '—'}
                tone={
                  tipmiReady && (data?.outbox?.dead ?? 0) > 0 ? 'bad' : 'muted'
                }
              />
              <DetailRow
                label="Status"
                value={tipmiReady ? (data?.outbox?.status ?? 'unknown') : '—'}
                tone={outboxTone(data, false)}
              />
              <button
                type="button"
                onClick={onOpenOutbox}
                className="text-[10px] font-medium text-zinc-300 underline-offset-2 hover:text-white hover:underline"
              >
                View backlog
              </button>
            </Section>

            {(data?.error || isError) && (
              <p className="rounded-lg border border-rose-400/20 bg-rose-400/[0.06] px-3 py-2 text-[11px] leading-relaxed text-rose-200/90">
                {data?.error ?? 'Failed to load infrastructure health'}
              </p>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Live media-manager + GPU + Coop + publication mode chips for Content Management.
 */
export function ContentInfrastructureHealth() {
  const { data, isLoading, isError, isFetching, refetch } = useInfrastructureHealth();
  const wakeGpu = useWakeGpu();
  const [outboxOpen, setOutboxOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [armedWake, setArmedWake] = useState<ArmedGpuWake | null>(null);
  const view = applyArmedGpuWake(data, armedWake, nowMs);
  const refresh = () => void refetch();
  const canWake = gpuWakeActionAvailable(view, nowMs);
  const wakeFail = gpuWakeFailureReason(view, nowMs);
  const wakeInFlight = gpuWakeInProgress(view, nowMs);
  const gpuReady = view?.gpu?.status === 'ready' || view?.gpu?.ready === true;
  const wakeDisabled = view?.gpu?.wake?.enabled === false && !gpuReady;
  const showWakeStatus =
    !gpuReady &&
    (isLoading ||
      Boolean(isError) ||
      view?.mediaManagerReachable === false ||
      wakeInFlight ||
      wakeDisabled ||
      wakeFail != null ||
      canWake ||
      wakeGpu.isPending);
  const wakeElapsed = gpuWakeElapsedLabel(view, nowMs);
  const wakeStatusLabel = isLoading || wakeGpu.isPending
    ? 'Wake · …'
    : wakeInFlight
      ? wakeElapsed
        ? `Wake · Waking · ${wakeElapsed}`
        : 'Wake · Waking'
      : wakeDisabled
        ? 'Wake · disabled'
        : !view?.mediaManagerReachable
          ? 'Wake · —'
          : wakeFail
            ? `Wake · ${wakeFail}`
            : 'Wake · idle';
  const wakeStatusTone: Tone = isLoading
    ? 'loading'
    : wakeDisabled
      ? 'muted'
      : wakeFail
        ? 'bad'
        : wakeInFlight || wakeGpu.isPending
          ? 'warn'
          : 'muted';
  const requestWake = () => {
    if (wakeGpu.isPending || gpuWakeInProgress(view, nowMs)) return;
    void wakeGpu
      .mutateAsync()
      .then((result) => {
        if (result?.status === 'enqueued' || result?.status === 'already_active') {
          const startedAt = wakeStartForAction(result, data, Date.now());
          if (startedAt) {
            setArmedWake({
              startedAt,
              workId: result.wake?.workId ?? result.workId,
              timeoutSec: result.wake?.timeoutSec ?? data?.gpu?.wake?.timeoutSec ?? 600,
            });
          }
        }
        wakeActionToast(result);
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to wake GPU');
      });
  };
  // Tick while wake or NodeClaim provisioning is in flight.
  const chipTicking =
    gpuWakeInProgress(view, nowMs) || gpuProvisioningInProgress(view, nowMs);
  useEffect(() => {
    if (!armedWake) return;
    if (data?.gpu?.status === 'ready' || data?.gpu?.ready === true) {
      setArmedWake(null);
      return;
    }
    if ((nowMs - Date.parse(armedWake.startedAt)) / 1000 >= armedWake.timeoutSec) {
      setArmedWake(null);
    }
  }, [armedWake, data?.gpu?.ready, data?.gpu?.status, nowMs]);
  useEffect(() => {
    if (!chipTicking) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [
    chipTicking,
    view?.gpu?.wake?.enqueuedAt,
    view?.gpu?.wake?.workId,
    view?.gpu?.capacity?.provisioningStartedAt,
  ]);
  // Only surface Pods when something is wrong — healthy state is in Details.
  const showPods =
    !isLoading &&
    (isError ||
      !data?.mediaManagerReachable ||
      (data.crashLoops?.total ?? 0) > 0);

  return (
    <Card className="h-full w-max max-w-full">
      <CardContent className="flex h-full items-center gap-3 p-3">
      <div className="flex min-w-0 flex-col justify-center gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip
          tone={policyTone(view, isLoading)}
          label={policyLabel(view, isLoading)}
          title={policyTitle(view)}
          tooltip={policyTooltip(view)}
        />
        <Chip
          tone={deepTone(view, isLoading, isError)}
          label={deepLabel(view, isLoading, isError)}
          title={deepTitle(view, isError)}
          tooltip={deepTooltip(view, isError)}
        />
        <Chip
          tone={mediaTone(view, isLoading, isError)}
          label={mediaLabel(view, isLoading, isError)}
          title={mediaTitle(view)}
        />
        <Chip
          tone={coopTone(view, isLoading, isError)}
          label={coopLabel(view, isLoading, isError)}
          title={coopTitle(view)}
        />
        <StackDetailsPopover
          data={view}
          isLoading={isLoading}
          isError={isError}
          isFetching={isFetching}
          onRefresh={refresh}
          onWake={requestWake}
          onOpenOutbox={() => setOutboxOpen(true)}
          waking={wakeGpu.isPending}
          nowMs={nowMs}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip
          tone={gpuTone(view, isLoading, isError, nowMs)}
          label={gpuLabel(view, isLoading, isError, nowMs)}
          title={gpuTitle(view, nowMs)}
          tooltip={gpuTooltip(view, nowMs)}
        />
        {showWakeStatus ? (
          <Chip
            tone={wakeStatusTone}
            label={wakeStatusLabel}
            title={gpuWakeSummary(view, nowMs) ?? 'gpu_wake status'}
            tooltip={
              <div>
                <p className="font-medium text-popover-foreground">Wake</p>
                <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                  {gpuWakeSummary(view, nowMs) ??
                    'Status only. Use Wake GPU to enqueue scale-from-zero.'}
                </p>
              </div>
            }
          />
        ) : null}
        {showPods ? (
          <Chip
            tone={crashLoopsTone(view, isLoading, isError)}
            label={crashLoopsLabel(view, isLoading, isError)}
            title={crashLoopsTitle(view)}
          />
        ) : null}
        <Chip
          tone={outboxTone(view, isLoading)}
          label={outboxLabel(view, isLoading)}
          title={outboxTitle(view)}
        />
      </div>
      </div>
      {canWake ? (
        <button
          type="button"
          onClick={requestWake}
          disabled={wakeGpu.isPending}
          title="Enqueue gpu_wake so KEDA scales the GPU pod. Does not wait for Ready."
          className="shrink-0 self-stretch border-l border-white/10 pl-3 text-[10px] font-semibold text-amber-300 hover:text-amber-200 disabled:opacity-50"
        >
          {wakeGpu.isPending ? 'Waking…' : 'Wake GPU'}
        </button>
      ) : null}
      </CardContent>
      <OutboxBacklogDialog open={outboxOpen} onOpenChange={setOutboxOpen} />
    </Card>
  );
}
